/**
 * AI prompt generation for story element creation (Section-wide suggestions)
 * Builds structured prompts and parses AI responses for story generation
 */

import type {
  Character,
  Encounter,
  GeneratorSettings,
  Location,
  LockState,
  Objective,
  SideQuest,
  Skill,
  SlotCounts,
  StorySeed,
  StorySeedDrafts,
} from "@/types/story.ts";
import type { StructuredPrompt } from "@/lib/ai/types.ts";
import {
  AMAZING_TALES_EXAMPLES,
  CHARACTER_EXAMPLES,
  LOCATION_EXAMPLES,
  OBJECTIVE_EXAMPLES,
  QUALITY_RULES,
  SETTING_EXAMPLES,
  SIDEQUEST_EXAMPLES,
  SKILL_EXAMPLES,
} from "./storyExamples.ts";
import { jsonrepair } from "jsonrepair";
import { z } from "zod";

/**
 * Format locked items for context in prompts
 */
function formatLockedContext(lockState: LockState, storySeed: StorySeed): string {
  const locked: string[] = [];

  if (lockState.storyName && storySeed.storyName) {
    locked.push(`Story Name: ${storySeed.storyName}`);
  }

  if (lockState.mainObjective && storySeed.mainObjective.description) {
    locked.push(`Main Objective: ${storySeed.mainObjective.description}`);
  }

  if (lockState.storyTwist && storySeed.storyTwist) {
    locked.push(`Story Twist: ${storySeed.storyTwist}`);
  }

  if (lockState.storyIntro && storySeed.storyIntro) {
    locked.push(`Story Intro: ${storySeed.storyIntro.substring(0, 100)}...`);
  }

  // Characters
  const lockedChars = storySeed.characters
    .filter((_: Character, i: number) => lockState.characters[i])
    .filter((c: Character) => c.name);
  if (lockedChars.length > 0) {
    locked.push(
      `Characters: ${lockedChars.map((c: Character) => `${c.name} (${c.role})`).join(", ")}`,
    );
  }

  // Locations
  const lockedLocs = storySeed.locations
    .filter((_: Location, i: number) => lockState.locations[i])
    .filter((l: Location) => l.name);
  if (lockedLocs.length > 0) {
    locked.push(`Locations: ${lockedLocs.map((l: Location) => l.name).join(", ")}`);
  }

  // Skills
  const lockedSkills = storySeed.skills
    .filter((_: Skill, i: number) => lockState.skills[i])
    .filter((s: Skill) => s.name);
  if (lockedSkills.length > 0) {
    locked.push(`Skills: ${lockedSkills.map((s: Skill) => s.name).join(", ")}`);
  }

  // Side Quests
  const lockedQuests = storySeed.sideQuests
    .filter((_: SideQuest, i: number) => lockState.sideQuests[i])
    .filter((q: SideQuest) => q.description);
  if (lockedQuests.length > 0) {
    locked.push(
      `Side Quests: ${
        lockedQuests.map((q: SideQuest, i: number) =>
          `${i + 1}. ${q.description.substring(0, 50)}...`
        ).join("; ")
      }`,
    );
  }

  return locked.length > 0 ? locked.join("\n") : "None yet - this is a fresh start!";
}

const MAX_TOKENS = {
  generation: 8192,
  step2: 4096,
  step3: 4096,
  step4: 4096,
  skillUses: 1024,
  storyName: 1024,
} as const;

const STEP4_EXAMPLES: Record<
  "twist" | "intro" | "sideQuest" | "character" | "location" | "encounter",
  string
> = {
  twist: `${OBJECTIVE_EXAMPLES}\n\n${QUALITY_RULES}`,
  intro: QUALITY_RULES,
  sideQuest: `${SIDEQUEST_EXAMPLES}\n\n${QUALITY_RULES}`,
  character: `${CHARACTER_EXAMPLES}\n\n${QUALITY_RULES}`,
  location: `${LOCATION_EXAMPLES}\n\n${QUALITY_RULES}`,
  encounter: QUALITY_RULES,
};

/**
 * Build Step 2 prompt: Generate skill suggestions + theme variant descriptions only
 */
export function buildStep2Prompt(
  settings: GeneratorSettings,
  skillSuggestionCount: number = 16,
  themeVariantCount: number = 4,
): StructuredPrompt {
  const { language, ageGroup, theme, additionalInstructions } = settings;
  const themeLine = theme ? `- Theme: ${theme}` : "";

  const system =
    `You are a creative storyteller for children. Generate story elements for an interactive adventure game.

${SKILL_EXAMPLES}

${SETTING_EXAMPLES}

${QUALITY_RULES}`;

  const user = `## Story Settings
- Language: ${language}
- Age Group: ${ageGroup}
${themeLine}
${additionalInstructions ? `- Additional Instructions: ${additionalInstructions}` : ""}

## Generate Suggestions
Generate ${skillSuggestionCount} diverse options for SKILLS and ${themeVariantCount} THEME VARIANT DESCRIPTIONS.

## Response Format Requirements:
Return ONLY a valid JSON object with the following structure:

{
  "skills": [
    { "name": "Skill Name 1", "emoji": "🎯", "application": "concrete action description" },
    ...${skillSuggestionCount} total skill options
  ],
  "themeVariant": [
    "On a pirate ship crossing stormy seas",
    "Treasure island full of hidden caves",
    "Crowded port city at night",
    "Time vortex leads to dinosaur-era shores",
    ...${themeVariantCount} total theme variant descriptions
  ]
}

IMPORTANT RULES:
1. Return ONLY valid JSON - no markdown code blocks, no explanations
2. CRITICAL JSON FORMATTING: All string values must be properly escaped, NEVER use double quotes inside strings
3. Generate exactly ${skillSuggestionCount} skill options and ${themeVariantCount} theme variant options
4. Keep language appropriate for ${ageGroup} age group
5. Skills MUST include "application" field with concrete action descriptions (e.g., "dodge thorns", "pick locks")
6. Theme variants should be slightly detailed (8-16 words) and focus on WHERE/WHEN or SETTING variations of the base theme
7. Variants can include mild mood/tone and sensory detail, but must NOT describe plot, quests, or specific characters
8. Good examples: "Old London sewers", "In-flight mystery after takeoff", "Medieval village with a witch hut"
9. All content must be in ${language} language
10. Create varied setting ideas, not reworded duplicates
11. Follow the Amazing Tales quality patterns - interconnected elements, emotional depth`;

  return { system, user, maxTokens: MAX_TOKENS.step2 };
}

/**
 * Build Step 3 prompt: Generate main objective options only, using locked skills + theme variant
 */
export function buildStep3Prompt(
  settings: GeneratorSettings,
  lockedSkills: Skill[],
  suggestionCount: number = 8,
): StructuredPrompt {
  const { language, ageGroup, theme, themeVariant, additionalInstructions } = settings;
  const themeLine = theme ? `- Theme: ${theme}` : "";
  const skillsLine = lockedSkills.map((s) => `${s.emoji} ${s.name}`).join(", ");
  const hasSkills = lockedSkills.length > 0;

  const system =
    `You are a creative storyteller for children. Generate main objective options for an interactive adventure game.

${OBJECTIVE_EXAMPLES}

${QUALITY_RULES}`;

  const user = `## Story Settings
- Language: ${language}
- Age Group: ${ageGroup}
${themeLine}
${themeVariant ? `- Theme Variant: ${themeVariant}` : ""}
${additionalInstructions ? `- Additional Instructions: ${additionalInstructions}` : ""}

## Locked Elements (use these for context):
- Skills: ${hasSkills ? skillsLine : "None"}

## Generate Main Objective Options
Generate ${suggestionCount} diverse main objective options. Each objective should:
- Fit the theme and theme variant
- Be age-appropriate for ${ageGroup}
- ${hasSkills ? "Be achievable using the locked skills" : "Not require specific skills"}
- ${hasSkills ? "Include short skill use ideas in skillUses" : "Keep skillUses as an empty array"}

## Response Format Requirements:
Return ONLY a valid JSON object with the following structure:

{
  "mainObjective": [
    { "description": "Find the lost treasure before the pirates arrive", "skillUses": ["Follow map clues", "Sneak past pirates"] },
    { "description": "Rescue the kidnapped prince from the dragon's lair", "skillUses": ["Distract the dragon", "Unlock the cage"] }
  ]
}

IMPORTANT RULES:
1. Return ONLY valid JSON - no markdown code blocks, no explanations
2. CRITICAL JSON FORMATTING: All string values must be properly escaped, NEVER use double quotes inside strings
3. Generate exactly ${suggestionCount} options
4. Keep language appropriate for ${ageGroup} age group
5. ${
    hasSkills
      ? "Include 3-5 entries in each skillUses array"
      : "Keep skillUses as an empty array in each item"
  }
6. Each skillUses entry must be a concrete scene-level action tied to THIS objective
7. BAD skillUses: "fighting", "sneaking", "using magic"
8. GOOD skillUses: "win a sword duel on the bridge", "steal the wizard hat during fireworks", "find the golden key in avalanche debris"
9. All content must be in ${language} language`;

  return { system, user, maxTokens: MAX_TOKENS.step3 };
}

/**
 * Build Step 4 field-specific prompt: Generate ONE type of element on demand
 */
export function buildStep4FieldPrompt(
  field: "twist" | "intro" | "sideQuest" | "character" | "location" | "encounter",
  settings: GeneratorSettings,
  lockedSkills: Skill[],
  mainObjective: Objective,
  suggestionCount: number = 8,
): StructuredPrompt {
  const { language, ageGroup, theme, themeVariant, additionalInstructions } = settings;
  const themeLine = theme ? `- Theme: ${theme}` : "";
  const skillsLine = lockedSkills.map((s) => `${s.emoji} ${s.name}`).join(", ");
  const hasSkills = lockedSkills.length > 0;

  const fieldPrompts = {
    twist: {
      key: "storyTwist",
      description: "story twist options (unexpected turns in the narrative)",
      example: '["twist1", "twist2", ...]',
    },
    intro: {
      key: "storyIntro",
      description: "story introduction paragraphs (2-3 engaging sentences)",
      example: '["intro paragraph 1", "intro paragraph 2", ...]',
    },
    sideQuest: {
      key: "sideQuests",
      description: "side quest options",
      example: hasSkills
        ? '[{ "description": "...", "skillUses": ["skill use 1", "skill use 2"] }, ...]'
        : '[{ "description": "...", "skillUses": [] }, ...]',
    },
    character: {
      key: "characters",
      description: "character options with roles and emojis",
      example: hasSkills
        ? '[{ "name": "...", "role": "...", "emoji": "👨", "skillUses": ["skill use 1", "skill use 2"] }, ...]'
        : '[{ "name": "...", "role": "...", "emoji": "👨", "skillUses": [] }, ...]',
    },
    location: {
      key: "locations",
      description: "location options with descriptions and emojis",
      example: hasSkills
        ? '[{ "name": "...", "description": "...", "emoji": "🏰", "skillUses": ["skill use 1", "skill use 2"] }, ...]'
        : '[{ "name": "...", "description": "...", "emoji": "🏰", "skillUses": [] }, ...]',
    },
    encounter: {
      key: "encounters",
      description: "encounter options (events, monsters, treasures)",
      example: hasSkills
        ? '[{ "description": "...", "type": "event", "skillUses": ["skill use 1", "skill use 2"] }, ...]'
        : '[{ "description": "...", "type": "event", "skillUses": [] }, ...]',
    },
  };

  const fieldConfig = fieldPrompts[field];

  const system =
    `You are a creative storyteller for children. Generate ${fieldConfig.description} for an interactive adventure game.

${STEP4_EXAMPLES[field]}`;

  const user = `## Story Settings
- Language: ${language}
- Age Group: ${ageGroup}
${themeLine}
${themeVariant ? `- Theme Variant: ${themeVariant}` : ""}
${additionalInstructions ? `- Additional Instructions: ${additionalInstructions}` : ""}

## Locked Elements (use these for context):
- Skills: ${hasSkills ? skillsLine : "None"}
- Main Objective: ${mainObjective.description}

## Generate ${fieldConfig.description}
Generate ${suggestionCount} diverse options that fit the theme and main objective.

## Response Format Requirements:
Return ONLY a valid JSON object with the following structure:

{
  "${fieldConfig.key}": ${fieldConfig.example}
}

IMPORTANT RULES:
1. Return ONLY valid JSON - no markdown code blocks, no explanations
2. CRITICAL JSON FORMATTING: All string values must be properly escaped, NEVER use double quotes inside strings
3. Generate exactly ${suggestionCount} options
4. Keep language appropriate for ${ageGroup} age group
5. ${
    hasSkills
      ? "For side quests, characters, locations, and encounters include a skillUses array with 3-5 concrete action phrases; for twists and intro, omit skillUses"
      : "Include skillUses: [] for side quests, characters, locations, and encounters; for twists and intro, omit skillUses"
  }
6. All content must be in ${language} language
7. Every skillUses phrase must describe a specific action in that exact element context
8. BAD skillUses: "fighting", "sneaking", "spotting treasure"
9. GOOD skillUses: "win a sword duel on the bridge", "steal the wizard hat during fireworks", "find the golden key in avalanche debris"
10. When skillUses are present, prefer format: "specific action in scene (related skill)"
11. Follow the Amazing Tales quality patterns - interconnected elements, emotional depth
12. CRITICAL CHARACTER RULES: Every character MUST have a proper name and ONE defining adjective (nervous, pedantic, cunning, enthusiastic, overconfident, etc.) in their name like "Name the Adjective Role". The role description must reflect how that adjective colors their behavior. NEVER use just a type like "Evil King" or "Shark Guard". Character names MUST be in ${language} — use names natural to that language, not English names.`;

  return { system, user, maxTokens: MAX_TOKENS.step4 };
}

export function buildStep4ContinuationPrompt(
  field: "twist" | "intro" | "sideQuest" | "character" | "location" | "encounter",
  settings: GeneratorSettings,
  lockedSkills: Skill[],
  mainObjective: Objective,
  suggestionCount: number = 8,
): StructuredPrompt {
  const { language, ageGroup, theme, themeVariant, additionalInstructions } = settings;
  const themeLine = theme ? `- Theme: ${theme}` : "";
  const skillsLine = lockedSkills.map((s) => `${s.emoji} ${s.name}`).join(", ");
  const hasSkills = lockedSkills.length > 0;

  const fieldPrompts = {
    twist: {
      key: "storyTwist",
      description: "story twist options (unexpected turns in the narrative)",
      example: '["twist1", "twist2", ...]',
    },
    intro: {
      key: "storyIntro",
      description: "story introduction paragraphs (2-3 engaging sentences)",
      example: '["intro paragraph 1", "intro paragraph 2", ...]',
    },
    sideQuest: {
      key: "sideQuests",
      description: "side quest options",
      example: '[{ "description": "..." }, ...]',
    },
    character: {
      key: "characters",
      description: "character options with roles and emojis",
      example: '[{ "name": "...", "role": "...", "emoji": "👨" }, ...]',
    },
    location: {
      key: "locations",
      description: "location options with descriptions and emojis",
      example: '[{ "name": "...", "description": "...", "emoji": "🏰" }, ...]',
    },
    encounter: {
      key: "encounters",
      description: "encounter options (events, monsters, treasures)",
      example: '[{ "description": "...", "type": "event" }, ...]',
    },
  };

  const fieldConfig = fieldPrompts[field];

  const system =
    `You are a creative storyteller for children. Generate ${fieldConfig.description} for an interactive adventure game.

${STEP4_EXAMPLES[field]}`;

  const user = `Continue the same story context.

## Story Settings
- Language: ${language}
- Age Group: ${ageGroup}
${themeLine}
${themeVariant ? `- Theme Variant: ${themeVariant}` : ""}
${additionalInstructions ? `- Additional Instructions: ${additionalInstructions}` : ""}

## Locked Elements (keep strictly consistent):
- Skills: ${hasSkills ? skillsLine : "None"}
- Main Objective: ${mainObjective.description}

Generate ${suggestionCount} diverse options for ${fieldConfig.description}.

## Response Format Requirements:
Return ONLY a valid JSON object with the following structure:

{
  "${fieldConfig.key}": ${fieldConfig.example}
}

IMPORTANT RULES:
1. Return ONLY valid JSON - no markdown code blocks, no explanations
2. CRITICAL JSON FORMATTING: All string values must be properly escaped, NEVER use double quotes inside strings
3. Generate exactly ${suggestionCount} options
4. Keep language appropriate for ${ageGroup} age group
5. ${
    hasSkills
      ? "For side quests, characters, locations, and encounters include a skillUses array with 3-5 concrete action phrases; for twists and intro, omit skillUses"
      : "Include skillUses: [] for side quests, characters, locations, and encounters; for twists and intro, omit skillUses"
  }
6. Every skillUses phrase must describe a specific action in that exact element context
7. Use format: "specific action in scene (related skill)" when skillUses are present
8. BAD skillUses: "fighting", "sneaking", "spotting treasure"
9. GOOD skillUses: "win a sword duel on the bridge (sword fighting)", "steal the wizard hat during fireworks (sneaking)", "find the golden key in avalanche debris (spotting treasures)"
10. Keep all options aligned to the locked main objective and locked skills
11. CRITICAL CHARACTER RULES: Every character MUST have a proper name and ONE defining adjective (nervous, pedantic, cunning, enthusiastic, overconfident, etc.) in their name like "Name the Adjective Role". The role description must reflect how that adjective colors their behavior. NEVER use just a type like "Evil King" or "Shark Guard". Character names MUST be in ${language} — use names natural to that language, not English names.`;

  return { system, user, maxTokens: MAX_TOKENS.step4 };
}

/**
 * Build prompt for generating skill usage suggestions for a specific item
 */
export function buildSkillUsesPrompt(
  settings: GeneratorSettings,
  lockedSkills: Skill[],
  itemType: "objective" | "sideQuest" | "character" | "location" | "encounter",
  itemDescription: string,
  suggestionCount: number = 4,
): StructuredPrompt {
  const { language, ageGroup, theme, themeVariant, additionalInstructions } = settings;
  const themeLine = theme ? `- Theme: ${theme}` : "";
  const skillsLine = lockedSkills.map((s) => `${s.emoji} ${s.name}`).join(", ");

  const system =
    "You are a creative storyteller for children. Generate skill usage suggestions for one specific story element.";

  const user = `## Story Settings
- Language: ${language}
- Age Group: ${ageGroup}
${themeLine}
${themeVariant ? `- Theme Variant: ${themeVariant}` : ""}
${additionalInstructions ? `- Additional Instructions: ${additionalInstructions}` : ""}

## Locked Skills (use only these):
${skillsLine}

## Target Element
- Type: ${itemType}
- Description: ${itemDescription}

## Generate Skill Uses
Generate ${suggestionCount} short action phrases that describe how the locked skills could be used for this element.

## Response Format Requirements:
Return ONLY a valid JSON object with the following structure:

{
  "skillUses": [
    "short action phrase 1",
    "short action phrase 2",
    "short action phrase 3",
    "short action phrase 4"
  ]
}

IMPORTANT RULES:
1. Return ONLY valid JSON - no markdown code blocks, no explanations
2. CRITICAL JSON FORMATTING: All string values must be properly escaped, NEVER use double quotes inside strings
3. Do NOT use trailing commas in arrays or objects
4. Generate exactly ${suggestionCount} entries in skillUses
5. Each entry must be a concrete scene-level action phrase (6-12 words)
6. Every entry must clearly match this exact element and situation
7. BAD: "fighting", "sneaking", "using magic"
8. GOOD: "win a sword duel on the bridge (sword fighting)", "steal the wizard hat during fireworks (sneaking)", "find the golden key in avalanche debris (spotting treasures)"
9. Use the locked skills as inspiration, but do not repeat the skill names verbatim
10. All content must be in ${language} language`;

  return { system, user, maxTokens: MAX_TOKENS.skillUses };
}

/**
 * Parse skill uses response
 */
export function parseSkillUsesResponse(response: string): string[] {
  try {
    let cleaned = response.trim();
    if (cleaned.startsWith("```")) {
      cleaned = cleaned.replace(/^```(?:json)?\n?/, "").replace(/\n?```$/, "");
    }

    let parsed;
    try {
      parsed = JSON.parse(cleaned);
    } catch {
      const repaired = jsonrepair(cleaned);
      parsed = JSON.parse(repaired);
    }

    const schema = z.object({ skillUses: z.array(z.string()) });
    const validated = schema.safeParse(parsed);
    if (!validated.success) {
      throw new Error(validated.error.message);
    }

    return validated.data.skillUses;
  } catch (error) {
    console.error("Failed to parse skill uses response:", error);
    throw new Error(
      `Failed to parse skill uses response: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
    );
  }
}

/**
 * Build the complete AI prompt for story generation with section-wide suggestions
 * (LEGACY - kept for backward compatibility)
 */
export function buildGenerationPrompt(
  storySeed: StorySeed,
  lockState: LockState,
  settings: GeneratorSettings,
  suggestionCount: number = 8,
): StructuredPrompt {
  const { language, ageGroup, theme, additionalInstructions } = settings;
  const themeLine = theme ? `- Theme: ${theme}` : "";
  const step2SkillsLine = settings.lockedSkills.map((s) => `${s.emoji} ${s.name}`).join(", ");
  const hasStep2Skills = settings.lockedSkills.length > 0;

  const system =
    `You are a creative storyteller for children. Generate story elements for an interactive story seed that will be used to create a playable adventure game.

${AMAZING_TALES_EXAMPLES}`;

  const user = `## Story Settings
- Language: ${language}
- Age Group: ${ageGroup}
${themeLine}
${additionalInstructions ? `- Additional Instructions: ${additionalInstructions}` : ""}

## Already Selected Elements (keep these in mind for context and consistency):
${formatLockedContext(lockState, storySeed)}

## Mandatory Step 2 Skills (if present):
- Skills: ${hasStep2Skills ? step2SkillsLine : "None"}

## Generate Suggestions
For each unlocked section below, generate ${suggestionCount} diverse options that the user can choose from. If a section is fully locked, return an empty array.

## Response Format Requirements:
Return ONLY a valid JSON object with the following structure:

{
  "storyName": ["Story Title 1", "Story Title 2", ..., "Story Title ${suggestionCount}"],
  "mainObjective": [
    { "description": "...", "skillUses": ["sneak past the sleeping guard", "decode the ancient map"] },
    ...${suggestionCount} total options
  ],
  "storyTwist": ["twist1", "twist2", ..., "twist${suggestionCount}"],
  "storyIntro": ["intro paragraph 1 (2-3 sentences)", ..., "intro paragraph ${suggestionCount}"],
  "sideQuests": [
    { "description": "quest 1", "skillUses": ["climb the crumbling wall", "convince the shopkeeper"] },
    ...${suggestionCount} total quest options
  ],
  "encounters": [
    { "description": "A fogbank reveals a ghost ship", "type": "event", "skillUses": ["track shapes through the fog", "signal allies with lantern code"] },
    ...${suggestionCount} total encounter options
  ],
  "skills": [
    { "name": "Skill Name 1", "emoji": "🎯", "application": "concrete action description" },
    ...${suggestionCount} total skill options
  ],
  "characters": [
    { "name": "Grizelda the Pedantic Witch", "role": "precise spellcaster who insists on exact ingredients", "emoji": "🧙‍♀️", "skillUses": ["challenge them to a duel", "befriend them with stories"] },
    ...${suggestionCount} total character options
  ],
  "locations": [
    { "name": "Location 1", "description": "description", "emoji": "🏰", "skillUses": ["navigate the dark tunnels", "spot hidden traps"] },
    ...${suggestionCount} total location options
  ]
}

IMPORTANT RULES:
1. Return ONLY valid JSON - no markdown code blocks, no explanations, no additional text
2. CRITICAL JSON FORMATTING: 
   - All string values must be properly escaped
   - NEVER use double quotes (") inside string values - use single quotes (') or omit them entirely
   - AVOID apostrophes in possessives - write "the pirates map" not "the pirate's map"
   - AVOID contractions - write "do not" not "don't", write "cannot" not "can't"
   - Your response must be parseable by JSON.parse() - verify this before responding
3. Generate exactly ${suggestionCount} options for each unlocked section
4. For locked sections, consider the locked elements when generating new options for unlocked sections
5. Keep language appropriate for ${ageGroup} age group
6. Make each option diverse and interesting - avoid repetitive ideas
7. Include emojis for skills, characters, and locations (use diverse, relevant emojis)
8. CRITICAL: skillUses must be short ACTION PHRASES describing how a skill is applied in this specific context.
   BAD (skill names): ["Swimming", "Being Brave", "Doing Magic"]
   GOOD (usage phrases): ["swim through the flooded tunnel", "face the dragon without flinching", "cast a shrinking spell on the door"]
9. Story intros should be 2-3 engaging sentences that hook the reader
10. All content must be in ${language} language
11. Ensure consistency with already selected locked elements
12. Story names should be catchy, age-appropriate titles (e.g., "The Pirate Treasure Map", "Mystery of the Enchanted Forest")
13. CRITICAL: Each skill MUST include an "application" field with a concrete action description (e.g., "dodge thorns", "pick locks", "outswim sharks")
14. Follow the Amazing Tales quality patterns shown above - interconnected elements, emotional depth, specific challenges
15. For Objective, Side Quests, Characters, Locations, and Encounters, skillUses should be actionable phrases tied to THAT EXACT element.
     BAD: "Sneaking" or "Use Swimming"
     GOOD: "sneak past the troll guarding this bridge", "swim through this underwater cavern", "convince this merchant to share secrets"
16. Prefer multiple skill uses per objective, side quest, character, location, and encounter (at least 3 when possible)
17. Avoid generic social filler actions like "convince the blacksmith to forge a shield"
18. If Step 2 locked skills are provided, skillUses across objective, side quests, characters, locations, and encounters should clearly map to those skills
19. When possible, use format "specific action in scene (related skill)" for each skillUses entry
20. CRITICAL CHARACTER RULES: Every character MUST have a proper name and ONE defining adjective (nervous, pedantic, cunning, enthusiastic, overconfident, etc.) in their name like "Name the Adjective Role". The role description must reflect how that adjective colors ALL their behavior. NEVER use just a type like "Evil King" or "Shark Guard". Character names MUST be in ${language} — use names natural to that language, not English names.`;

  return { system, user, maxTokens: MAX_TOKENS.generation };
}

/**
 * Parse Step 2 response: skills + themeVariant
 */
export function parseStep2Response(response: string): {
  skills: Skill[];
  themeVariant: string[];
} {
  try {
    let cleaned = response.trim();
    if (cleaned.startsWith("```")) {
      cleaned = cleaned.replace(/^```(?:json)?\n?/, "").replace(/\n?```$/, "");
    }

    let parsed;
    try {
      parsed = JSON.parse(cleaned);
    } catch {
      const repaired = jsonrepair(cleaned);
      parsed = JSON.parse(repaired);
    }

    const skillSchema = z.object({
      name: z.string(),
      emoji: z.string(),
      application: z.string().optional(),
    });

    const step2Schema = z.object({
      skills: z.array(skillSchema),
      themeVariant: z.array(z.string()),
    });

    const validated = step2Schema.safeParse(parsed);
    if (!validated.success) {
      throw new Error(validated.error.message);
    }

    return {
      skills: validated.data.skills,
      themeVariant: validated.data.themeVariant,
    };
  } catch (error) {
    console.error("Failed to parse Step 2 response:", error);
    throw new Error(
      `Failed to parse Step 2 response: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
    );
  }
}

/**
 * Parse Step 3 response: main objective options
 */
export function parseStep3Response(response: string): Objective[] {
  try {
    let cleaned = response.trim();
    if (cleaned.startsWith("```")) {
      cleaned = cleaned.replace(/^```(?:json)?\n?/, "").replace(/\n?```$/, "");
    }

    let parsed;
    try {
      parsed = JSON.parse(cleaned);
    } catch {
      const repaired = jsonrepair(cleaned);
      parsed = JSON.parse(repaired);
    }

    const objectiveSchema = z.object({
      description: z.string(),
      skillUses: z.array(z.string()).optional().default([]),
    });

    const step3Schema = z.object({
      mainObjective: z.array(objectiveSchema),
    });

    const validated = step3Schema.safeParse(parsed);
    if (!validated.success) {
      throw new Error(validated.error.message);
    }

    return validated.data.mainObjective;
  } catch (error) {
    console.error("Failed to parse Step 3 response:", error);
    throw new Error(
      `Failed to parse Step 3 response: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
    );
  }
}

/**
 * Parse Step 4 field response: single field type
 */
export function parseStep4FieldResponse(
  response: string,
  field: "twist" | "intro" | "sideQuest" | "character" | "location" | "encounter",
): Character[] | Location[] | SideQuest[] | Encounter[] | string[] {
  try {
    let cleaned = response.trim();
    if (cleaned.startsWith("```")) {
      cleaned = cleaned.replace(/^```(?:json)?\n?/, "").replace(/\n?```$/, "");
    }

    let parsed;
    try {
      parsed = JSON.parse(cleaned);
    } catch {
      const repaired = jsonrepair(cleaned);
      parsed = JSON.parse(repaired);
    }

    if (field === "twist") {
      const schema = z.object({ storyTwist: z.array(z.string()) });
      const validated = schema.safeParse(parsed);
      if (!validated.success) throw new Error(validated.error.message);
      return validated.data.storyTwist;
    }

    if (field === "intro") {
      const schema = z.object({ storyIntro: z.array(z.string()) });
      const validated = schema.safeParse(parsed);
      if (!validated.success) throw new Error(validated.error.message);
      return validated.data.storyIntro;
    }

    if (field === "sideQuest") {
      const sideQuestSchema = z.object({
        description: z.string(),
        skillUses: z.array(z.string()).optional().default([]),
      });
      const schema = z.object({ sideQuests: z.array(sideQuestSchema) });
      const validated = schema.safeParse(parsed);
      if (!validated.success) throw new Error(validated.error.message);
      return validated.data.sideQuests;
    }

    if (field === "character") {
      const characterSchema = z.object({
        name: z.string(),
        role: z.string(),
        emoji: z.string(),
        skillUses: z.array(z.string()).optional().default([]),
      });
      const schema = z.object({ characters: z.array(characterSchema) });
      const validated = schema.safeParse(parsed);
      if (!validated.success) throw new Error(validated.error.message);
      return validated.data.characters;
    }

    if (field === "location") {
      const locationSchema = z.object({
        name: z.string(),
        description: z.string(),
        emoji: z.string(),
        skillUses: z.array(z.string()).optional().default([]),
      });
      const schema = z.object({ locations: z.array(locationSchema) });
      const validated = schema.safeParse(parsed);
      if (!validated.success) throw new Error(validated.error.message);
      return validated.data.locations;
    }

    if (field === "encounter") {
      const encounterSchema = z.object({
        description: z.string(),
        type: z.string().optional(),
        skillUses: z.array(z.string()).optional().default([]),
      });
      const schema = z.object({ encounters: z.array(encounterSchema) });
      const validated = schema.safeParse(parsed);
      if (!validated.success) throw new Error(validated.error.message);
      return validated.data.encounters;
    }

    throw new Error(`Unknown field: ${field}`);
  } catch (error) {
    console.error(`Failed to parse Step 4 ${field} response:`, error);
    throw new Error(
      `Failed to parse Step 4 ${field} response: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
    );
  }
}

/**
 * Build prompt for generating story name suggestions
 */
export function buildStoryNamePrompt(
  settings: GeneratorSettings,
  mainObjective: Objective,
  suggestionCount: number = 8,
): StructuredPrompt {
  const { language, ageGroup, theme, themeVariant, additionalInstructions, mainCharacterName } =
    settings;
  const themeLine = theme ? `- Theme: ${theme}` : "";

  const system = "You are a creative storyteller for children. Generate catchy story titles.";

  const user = `## Story Settings
- Language: ${language}
- Age Group: ${ageGroup}
${themeLine}
${themeVariant ? `- Theme Variant: ${themeVariant}` : ""}
${mainCharacterName ? `- Main Character: ${mainCharacterName}` : ""}
${additionalInstructions ? `- Additional Instructions: ${additionalInstructions}` : ""}

## Main Objective
- ${mainObjective.description}

## Generate Story Names
Generate ${suggestionCount} story name options. Titles should be short, memorable, and age-appropriate.

## Response Format Requirements:
Return ONLY a valid JSON object with the following structure:

{
  "storyName": [
    "Story Title 1",
    "Story Title 2",
    ...${suggestionCount} total options
  ]
}

IMPORTANT RULES:
1. Return ONLY valid JSON - no markdown code blocks, no explanations
2. CRITICAL JSON FORMATTING: All string values must be properly escaped, NEVER use double quotes inside strings
3. Generate exactly ${suggestionCount} options
4. All content must be in ${language} language`;

  return { system, user, maxTokens: MAX_TOKENS.storyName };
}

/**
 * Parse story name response
 */
export function parseStoryNameResponse(response: string): string[] {
  try {
    let cleaned = response.trim();
    if (cleaned.startsWith("```")) {
      cleaned = cleaned.replace(/^```(?:json)?\n?/, "").replace(/\n?```$/, "");
    }

    let parsed;
    try {
      parsed = JSON.parse(cleaned);
    } catch {
      const repaired = jsonrepair(cleaned);
      parsed = JSON.parse(repaired);
    }

    const schema = z.object({ storyName: z.array(z.string()) });
    const validated = schema.safeParse(parsed);
    if (!validated.success) {
      throw new Error(validated.error.message);
    }

    return validated.data.storyName;
  } catch (error) {
    console.error("Failed to parse story name response:", error);
    throw new Error(
      `Failed to parse story name response: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
    );
  }
}

/**
 * Parse and validate AI response into StorySeedDrafts (section-wide)
 */
export function parseAIResponse(response: string, _slotCounts: SlotCounts): StorySeedDrafts {
  try {
    // Remove markdown code blocks if present
    let cleaned = response.trim();
    if (cleaned.startsWith("```")) {
      cleaned = cleaned.replace(/^```(?:json)?\n?/, "").replace(/\n?```$/, "");
    }

    let parsed;
    try {
      parsed = JSON.parse(cleaned);
    } catch (jsonError) {
      // Enhanced error reporting for JSON parse failures
      const error = jsonError as Error;
      console.warn("Initial JSON parse failed, attempting repair...", {
        message: error.message,
        responseLength: cleaned.length,
      });

      // Try to repair the JSON
      const repaired = jsonrepair(cleaned);
      console.log("Attempting to parse repaired JSON (length:", repaired.length, ")");

      try {
        parsed = JSON.parse(repaired);
        console.log("✓ Successfully parsed repaired JSON");
      } catch (repairError) {
        const repairErr = repairError as Error;
        console.error("JSON Parse Error Details (after repair attempt):", {
          originalError: error.message,
          repairError: repairErr.message,
          responsePreview: cleaned.substring(0, 500),
          repairedPreview: repaired.substring(0, 500),
          responseLength: cleaned.length,
          repairedLength: repaired.length,
        });

        // Try to identify the problematic area
        const match = repairErr.message.match(/position (\d+)/);
        if (match) {
          const position = parseInt(match[1]);
          const start = Math.max(0, position - 100);
          const end = Math.min(repaired.length, position + 100);
          const context = repaired.substring(start, end);
          console.error("Context around error position:", context);
          console.error("Error at character:", repaired[position]);
        }

        throw new Error(
          `JSON parsing failed even after repair: ${repairErr.message}. Check console for details.`,
        );
      }
    }

    const skillSchema = z.object({
      name: z.string(),
      emoji: z.string(),
      application: z.string().optional(),
    });

    const objectiveSchema = z.object({
      description: z.string(),
      skillUses: z.array(z.string()),
    });

    const characterSchema = z.object({
      name: z.string(),
      role: z.string(),
      emoji: z.string(),
      skillUses: z.array(z.string()),
    });

    const locationSchema = z.object({
      name: z.string(),
      description: z.string(),
      emoji: z.string(),
      skillUses: z.array(z.string()),
    });

    const sideQuestSchema = z.object({
      description: z.string(),
      skillUses: z.array(z.string()),
    });

    const encounterSchema = z.object({
      description: z.string(),
      type: z.string().optional(),
      skillUses: z.array(z.string()).optional().default([]),
    });

    const draftSchema = z.object({
      storyName: z.array(z.string()).optional(),
      mainObjective: z.array(objectiveSchema).optional(),
      storyTwist: z.array(z.string()).optional(),
      storyIntro: z.array(z.string()).optional(),
      sideQuests: z.array(sideQuestSchema).optional(),
      encounters: z.array(encounterSchema).optional(),
      skills: z.array(skillSchema).optional(),
      characters: z.array(characterSchema).optional(),
      locations: z.array(locationSchema).optional(),
    });

    const validated = draftSchema.safeParse(parsed);
    if (!validated.success) {
      throw new Error(validated.error.message);
    }

    // Validate structure
    if (!parsed || typeof parsed !== "object") {
      throw new Error("Response is not a valid object");
    }

    const safe = validated.data;

    // Build draft items with validation - now section-wide
    const drafts: StorySeedDrafts = {
      storyName: {
        options: safe.storyName ?? [],
      },
      mainObjective: {
        options: safe.mainObjective ?? [],
      },
      storyTwist: {
        options: safe.storyTwist ?? [],
      },
      storyIntro: {
        options: safe.storyIntro ?? [],
      },
      themeVariant: {
        options: [],
      },
      sideQuests: {
        options: safe.sideQuests ?? [],
      },
      encounters: {
        options: safe.encounters ?? [],
      },
      skills: {
        options: safe.skills ?? [],
      },
      characters: {
        options: safe.characters ?? [],
      },
      locations: {
        options: safe.locations ?? [],
      },
    };

    return drafts;
  } catch (error) {
    console.error("Failed to parse AI response:", error);
    throw new Error(
      `Failed to parse AI response: ${error instanceof Error ? error.message : "Unknown error"}`,
    );
  }
}
